import React from 'react';
import VoxSmythIcons from './VoxSmythIcons';
import googleAnalytics from './services/googleAnalytics';

class VoxSmythErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { 
      hasError: true,
      errorId: Date.now().toString(36) // Unique error ID
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('VoxSmyth Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Track error with Google Analytics
    googleAnalytics.trackEvent('react_error_boundary', {
      category: 'error_tracking',
      error_message: error.message,
      error_id: this.state.errorId,
      page_url: window.location.href
    });
  }

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '400px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px',
          backgroundColor: '#1A202C',
          borderRadius: '12px',
          border: '1px solid #374151',
          color: '#FFFFFF',
          fontFamily: 'Inter, -apple-system, sans-serif',
          textAlign: 'center',
          margin: '20px 0'
        }}>
          {/* Error Icon */}
          <div style={{
            width: '80px',
            height: '80px',
            backgroundColor: '#2C3A4C',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '24px',
            border: '2px solid #D7B377'
          }}>
            <VoxSmythIcons.AlertTriangle size={32} color="#D7B377" />
          </div>

          {/* Error Title */}
          <h3 style={{
            fontFamily: 'DM Serif Display, Georgia, serif',
            fontSize: '24px',
            fontWeight: '400',
            color: '#D7B377',
            marginBottom: '12px',
            lineHeight: '1.2'
          }}>
            Something Went Wrong
          </h3>

          {/* Error Description */}
          <p style={{
            fontSize: '16px',
            color: '#CBD5E0',
            marginBottom: '24px',
            maxWidth: '500px',
            lineHeight: '1.5'
          }}>
            We encountered an unexpected error while loading this component. 
            Our team has been notified and is working on a fix.
          </p>

          {/* Error ID */}
          <p style={{
            fontSize: '12px',
            color: '#A0AEC0',
            fontFamily: 'Monaco, monospace',
            marginBottom: '24px'
          }}>
            Error ID: {this.state.errorId}
          </p>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <button
              onClick={this.handleRetry}
              style={{
                padding: '12px 24px',
                backgroundColor: '#D7B377',
                color: '#1A202C',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                minWidth: '120px'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#E2D7C4'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#D7B377'}
            >
              Try Again
            </button>
            
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '12px 24px',
                backgroundColor: 'transparent',
                color: '#D7B377',
                border: '1px solid #D7B377',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                minWidth: '120px'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#D7B37720';
                e.target.style.borderColor = '#E2D7C4';
                e.target.style.color = '#E2D7C4';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.borderColor = '#D7B377';
                e.target.style.color = '#D7B377';
              }}
            >
              Refresh Page
            </button>
          </div>

          {/* Development Error Details */}
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details style={{
              marginTop: '24px',
              padding: '16px',
              backgroundColor: '#2C3A4C',
              borderRadius: '8px',
              border: '1px solid #374151',
              width: '100%',
              maxWidth: '600px',
              fontSize: '12px',
              fontFamily: 'Monaco, monospace'
            }}>
              <summary style={{ 
                cursor: 'pointer', 
                color: '#D7B377', 
                marginBottom: '12px',
                fontWeight: '600'
              }}>
                Developer Error Details
              </summary>
              <div style={{ color: '#EF4444', marginBottom: '12px' }}>
                <strong>Error:</strong> {this.state.error.message}
              </div>
              <div style={{ color: '#CBD5E0', whiteSpace: 'pre-wrap', fontSize: '11px' }}>
                <strong>Stack Trace:</strong>
                {this.state.error.stack}
              </div>
              {this.state.errorInfo && (
                <div style={{ color: '#A0AEC0', whiteSpace: 'pre-wrap', fontSize: '11px', marginTop: '12px' }}>
                  <strong>Component Stack:</strong>
                  {this.state.errorInfo.componentStack}
                </div>
              )}
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default VoxSmythErrorBoundary;